import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import { majorScale, Section, Spinner, Table, useToast } from '../../../npaper';
import DateFilter from '../../../npaper/DateFilter';
import { FormattedUnit } from '../../../npaper/FormattedUnit';
import { ICompany, IProduct, useStores } from '../../../stores';
import { CompanyThumbnail } from '../../companies';
import querystring from 'querystring';

interface IProductStatistics {
  customers: {
    customer: number | string;
    quantity: number;
    times_purchased: number;
    first_purchased: string;
    last_purchased: string;
  }[];
  amounts: {
    month: string;
    quantity: string;
    amount: number;
    cost_price: number;
    cm: number;
  }[];
}

const ProductCustomers = ({ product }: { product: IProduct }) => {
  const intl = useIntl();
  const { productStore } = useStores();
  const toaster = useToast();
  const [statistics, setStatistics] = useState<IProductStatistics>();

  const [dateRange, setDateRange] =
    useState<{ from: Date; to?: Date } | null>(null);

  useEffect(() => {
    let isSub = true;

    const queryObj = dateRange
      ? {
          start_date: dateRange.from.toISOString(),
          end_date: dateRange.to ? dateRange.to.toISOString() : undefined,
        }
      : {};

    const query = querystring.encode(queryObj);
    productStore.env.api
      .get<IProductStatistics>(
        `${productStore.apiPath}/${product.id}/statistics/${
          query ? '?' + query : ''
        }`
      )
      .then((res) => isSub && setStatistics(res.data))
      .catch((err) => toaster.showDefaultError(err));

    return () => {
      isSub = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, dateRange]);

  return (
    <Section
      title={intl.formatMessage({ defaultMessage: 'Customers' })}
      actions={<DateFilter onSelect={(range) => setDateRange(range)} />}
    >
      {!statistics ? (
        <Spinner size={16} />
      ) : (
        <>
          {statistics.customers.length === 0 ? (
            <Box is="p" className="muted">
              <FormattedMessage defaultMessage="Nothing to show yet" />
            </Box>
          ) : (
            <Table marginTop={majorScale(-2)}>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage defaultMessage="Customer" />
                  </th>
                  <th className="right">
                    <FormattedMessage defaultMessage="Quantity" />
                  </th>
                  <th className="right">
                    <FormattedMessage defaultMessage="Times purchased" />
                  </th>
                  <th className="right">
                    <FormattedMessage defaultMessage="First purchased" />
                  </th>
                  <th className="right">
                    <FormattedMessage defaultMessage="Last purchased" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {statistics.customers.map((purchase, i) => (
                  <RenderRow key={i} row={purchase} product={product} />
                ))}
              </tbody>
              <tfoot></tfoot>
            </Table>
          )}
        </>
      )}
    </Section>
  );
};

const RenderRow = ({
  row,
  product,
}: {
  row: IProductStatistics['customers'][0];
  product: IProduct;
}) => {
  const { companyStore } = useStores();
  const [customer, setCustomer] = useState<ICompany>();

  useEffect(() => {
    let isSub = true;
    companyStore
      .getItemNaiveAsync(row.customer)
      .then((customer) => isSub && setCustomer(customer));
    return () => {
      isSub = false;
    };
  }, [companyStore, row.customer]);

  return (
    <tr>
      <td>
        {customer ? (
          <CompanyThumbnail company={customer} />
        ) : (
          <Spinner size={16} />
        )}
      </td>
      <td className="mono right">
        <FormattedUnit value={row.quantity} unit={product.unit} />
      </td>
      <td className="mono right">{row.times_purchased}</td>
      <td className="right">
        <FormattedDate value={row.first_purchased} format="default" />
      </td>
      <td className="right">
        <FormattedDate value={row.last_purchased} format="default" />
      </td>
    </tr>
  );
};

export default ProductCustomers;
